import React, { useEffect, useState, memo } from 'react';
import { useMousePosition } from '../hooks/useMousePosition';
import { useScrollProgress } from '../hooks/useScrollProgress';
import { ThreeBackground } from '../components/ThreeBackground';
import { FloatingNav } from '../components/FloatingNav';
import { Hero } from '../components/Hero';
import { Section } from '../components/Section';
import { WaveformDivider } from '../components/WaveformDivider';
import { EcosystemDiagram } from '../components/EcosystemDiagram';
import { ContactFooter } from '../components/ContactFooter';
const NAV_ITEMS = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'problem',
  label: 'Problem'
}, {
  id: 'concept',
  label: 'Concept'
}, {
  id: 'spatial',
  label: 'Spatial'
}, {
  id: 'residency',
  label: 'Residency'
}, {
  id: 'status',
  label: 'Status'
}, {
  id: 'artist',
  label: 'Artist'
}, {
  id: 'ecosystem',
  label: 'Ecosystem'
}, {
  id: 'questions',
  label: 'Questions'
}, {
  id: 'contact',
  label: 'Contact'
}];
export function NoiseLullabies() {
  const mousePosition = useMousePosition();
  const scrollProgress = useScrollProgress();
  const [activeSection, setActiveSection] = useState<string>('overview');
  // Intersection Observer for scroll reveals and active nav item
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px -40% 0px',
      threshold: 0.1
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        // Handle reveal animation
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          // Update active nav item if it's a section with an ID
          if (entry.target.id) {
            setActiveSection(entry.target.id);
          }
        }
      });
    }, observerOptions);
    const sections = document.querySelectorAll('.reveal-section');
    sections.forEach(section => observer.observe(section));
    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);
  return <div className="relative w-full min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-coral/30 selection:text-coral" data-id="element-68">
      {/* 3D Background Layer */}
      <ThreeBackground mousePosition={mousePosition} scrollProgress={scrollProgress} data-id="element-69" />

      {/* Navigation */}
      <FloatingNav items={NAV_ITEMS} activeId={activeSection} data-id="element-70" />

      {/* Content Layer */}
      <main className="content-layer w-full flex flex-col items-center" data-id="element-71">
        <Hero data-id="element-72" />

        <Section id="overview" title="Project Overview" data-id="element-73">
          <p data-id="element-74">
            Noise Lullabies is an ambient music and artistic research project by
            Akshay Gite under the moniker{' '}
            <span className="text-coral" data-id="element-75">sic</span>. It explores lullabies as a
            universal cultural practice and reinterprets them through
            experimental composition, field recordings, bass improvisation, tape
            loops, and spatial sound design. The project exists both as an album
            and as a live, immersive listening format for galleries, radio,
            listening spaces, and experimental music festivals.
          </p>
        </Section>

        <WaveformDivider data-id="element-76" />

        <Section id="problem" title="Cultural Problem" data-id="element-77">
          <p data-id="element-78">
            Lullabies are among the oldest forms of human music and oral
            tradition, yet they are rarely preserved or treated as culturally
            significant sonic material. They are often passed informally between
            caregivers and children and disappear as migration, language
            erosion, and social change reshape cultural life. Noise Lullabies
            responds to this by treating lullabies as forms of cultural memory
            and affective transmission, recontextualized through contemporary
            sound practice.
          </p>
        </Section>

        <WaveformDivider data-id="element-79" />

        <Section id="concept" title="Core Concept" data-id="element-80">
          <p data-id="element-81">
            The central proposition is that a lullaby is not only a song, but an
            act that produces affection. The project does not archive lullabies
            as documentary objects. Instead, it resynthesizes fragments,
            textures, and traces of them into new ambient environments. Core
            sonic methods include field recordings, drones, bass improvisation,
            tape loops, degraded textures, and spatial composition.
          </p>
        </Section>

        <WaveformDivider data-id="element-82" />

        <Section id="spatial" title="Spatial Concept for Vestre" data-id="element-83">
          <p data-id="element-84">
            The Vestre residency would allow the work to develop into a fully
            spatial listening environment using an 8-channel setup. The project
            explores LFO-driven panning, slow rotational movement, low
            sub-frequencies, and beating patterns that can be physically felt in
            the room. The space itself becomes a resonating body. Different
            channels carry different sonic materials such as lullaby fragments,
            vocal traces, drones, and bass textures, creating shifting
            perspectives depending on where the listener is positioned.
          </p>
        </Section>

        <WaveformDivider data-id="element-85" />

        <Section id="residency" title="Residency Intention" data-id="element-86">
          <p data-id="element-87">
            The residency would be used to test how spatial sound can carry or
            transform the affective qualities of a lullaby, particularly through
            proximity, movement, and bodily orientation.
          </p>
          <p data-id="element-88">
            One area of exploration involves placing low-frequency sources
            closer to the body, potentially distributing subwoofers across the
            floor or seating areas so sound is felt as much as heard. Another
            involves designing listening positions beyond upright frontal
            listening, allowing audiences to lie down or move through the room,
            with speakers lowered or angled accordingly. The multichannel system
            enables distributed perspectives where lullaby fragments are
            separated yet connected, and the project asks how these layers shift
            between intimate and collective experience as listeners move.
          </p>
        </Section>

        <WaveformDivider data-id="element-89" />

        <Section id="status" title="Current Status" data-id="element-90">
          <p data-id="element-91">
            The project is in active development. Several compositions have been
            developed as demos. The work is moving toward a cohesive album, with
            mastering planned in collaboration with James Ginzburg of Emptyset,
            whose work informs the low-frequency and spatial direction of the
            project.
          </p>
        </Section>

        <WaveformDivider data-id="element-92" />

        <Section id="artist" title={<span data-id="element-94">
              Artistic Approach <span className="text-coral" data-id="element-95">sic</span>
            </span>} data-id="element-93">
          <p data-id="element-96">
            <span className="text-coral" data-id="element-97">sic</span> is the artistic moniker of
            Akshay Gite, a sound artist, composer, designer, and researcher
            working at the intersection of experimental music, cultural memory,
            and spatial sound. The practice draws from ambient music,
            jazz-influenced bass improvisation, field recording, synthesis, and
            research into oral traditions, migration, and the sonic forms
            through which care and affection are transmitted across cultures.
          </p>
          <p data-id="element-98">
            This work is informed by movement between India and Sweden, where
            language, memory, and oral traditions shift across contexts.
          </p>
        </Section>

        <WaveformDivider data-id="element-99" />

        <section id="ecosystem" className="reveal-section w-full py-24 flex justify-center" data-id="element-100">
          <EcosystemDiagram data-id="element-101" />
        </section>

        <WaveformDivider data-id="element-102" />

        <Section id="questions" title="Open Questions" data-id="element-103">
          <ul className="space-y-4 list-none pl-0" data-id="element-104">
            <li className="flex items-start" data-id="element-105">
              <span className="text-coral mr-4 mt-1.5 opacity-50 text-sm" data-id="element-106">
                /
              </span>
              <span data-id="element-107">Completion of the album</span>
            </li>
            <li className="flex items-start" data-id="element-108">
              <span className="text-coral mr-4 mt-1.5 opacity-50 text-sm" data-id="element-109">
                /
              </span>
              <span data-id="element-110">Further material collection</span>
            </li>
            <li className="flex items-start" data-id="element-111">
              <span className="text-coral mr-4 mt-1.5 opacity-50 text-sm" data-id="element-112">
                /
              </span>
              <span data-id="element-113">Spatial testing in live environments</span>
            </li>
            <li className="flex items-start" data-id="element-114">
              <span className="text-coral mr-4 mt-1.5 opacity-50 text-sm" data-id="element-115">
                /
              </span>
              <span data-id="element-116">Determining release format</span>
            </li>
            <li className="flex items-start" data-id="element-117">
              <span className="text-coral mr-4 mt-1.5 opacity-50 text-sm" data-id="element-118">
                /
              </span>
              <span data-id="element-119">
                Identifying collaborators for visuals, curation, and
                installation contexts
              </span>
            </li>
          </ul>
        </Section>

        <ContactFooter id="contact" data-id="element-120" />
      </main>
    </div>;
}